<template>
  <!-- Safety (MSR): sin fuente automática. Algunos KPIs se derivan de la tabla de fases;
       el resto (inspecciones, observaciones, LTI, auditoría) se cumplimentan a mano. -->
  <summary-card :title="$t('prm_dash_safety_title')" :subtitle="$t('prm_dash_safety_subtitle')" :mode="mode" :mark="true" chip="none">
    <template #icon><b-icon pack="fas" icon="shield-halved" size="is-small"></b-icon></template>

    <!-- Resumen (KPIs): derivados de la tabla (cumplimiento, OSH) + manuales. -->
    <div class="tiles">
      <div v-for="t in tiles" :key="t.key" class="tile" :class="{ derived: t.derived }">
        <span class="tile-label">{{ $t(t.label) }}</span>
        <span class="tile-value">
          <template v-if="t.derived">
            <span :style="{ color: t.color }">{{ t.value }}</span><source-chip v-if="mode" source="calc"></source-chip>
          </template>
          <manual-field v-else :value="val(t.key)" :type="t.type || 'text'" :view-class="t.cls"
            @save="(v) => $emit('save-field', { path: 'safety.' + t.key, value: v })"></manual-field>
        </span>
      </div>
    </div>

    <!-- Fases del protocolo MSR (tabla editable con badges, barra y semáforo). -->
    <div class="tbl-wrap">
      <table :class="{ mark: mode }">
        <thead>
          <tr>
            <th>{{ $t('prm_dash_safety_col_phase') }}</th>
            <th class="c">{{ $t('prm_summary_col_status') }}</th>
            <th>{{ $t('prm_dash_safety_col_compliance') }}</th>
            <th class="c">{{ $t('prm_dash_safety_col_open') }}</th>
            <th class="c">{{ $t('prm_dash_safety_col_closed') }}</th>
            <th class="c">{{ $t('prm_dash_safety_col_review') }}</th>
            <th class="c"></th>
            <th class="c"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in localRows" :key="row._k">
            <td><manual-field :value="row.phase" @save="(v) => onCell(i, 'phase', v)"></manual-field></td>
            <td class="c">
              <manual-field :value="row.status" :options="statusOptions"
                :view-class="'st-badge st-' + (row.status || 'not_started')" @save="(v) => onCell(i, 'status', v)"></manual-field>
            </td>
            <td class="comp">
              <span class="comp-in">
                <span class="comp-num"><manual-field :value="row.compliance" @input="(v) => setLive(row._k, v)" @save="(v) => onCell(i, 'compliance', v)"></manual-field></span>
                <span class="pct">%</span>
                <bar :value="compVal(row)" :color="stateColor(row)"></bar>
              </span>
            </td>
            <td class="c"><manual-field :value="row.openIncidents" @save="(v) => onCell(i, 'openIncidents', v)"></manual-field></td>
            <td class="c"><manual-field :value="row.closedIncidents" @save="(v) => onCell(i, 'closedIncidents', v)"></manual-field></td>
            <td class="c"><manual-field :value="row.lastReview" type="date" @save="(v) => onCell(i, 'lastReview', v)"></manual-field></td>
            <td class="c"><sem-dot :state="phaseColor(row)"></sem-dot></td>
            <td class="c">
              <button class="row-remove" v-tip="$t('prm_summary_remove_row')" @click="removeRow(i)">
                <b-icon pack="fas" icon="trash-can" size="is-small"></b-icon>
              </button>
            </td>
          </tr>
          <tr v-if="!localRows.length"><td class="empty" colspan="8">{{ $t('prm_summary_empty') }}</td></tr>
        </tbody>
      </table>
    </div>

    <div class="tbl-footer">
      <button class="row-add" @click="addRow">
        <b-icon pack="fas" icon="plus" size="is-small"></b-icon> {{ $t('prm_dash_safety_add_phase') }}
      </button>
    </div>
  </summary-card>
</template>

<script>
  import SummaryCard from '../summary/summary-card.vue'
  import SourceChip from '../summary/source-chip.vue'
  import ManualField from '../summary/manual-field.vue'
  import SemDot from './sem-dot.vue'
  import Bar from './bar.vue'
  import { PHASE_STATUSES, phaseColor, SEM } from './dashboard-model'
  import { tip } from '../shared/tip'

  let keySeq = 0

  export default {
    name: 'PrmDashSafety',

    components: { SummaryCard, SourceChip, ManualField, SemDot, Bar },

    directives: { tip },

    props: {
      safety: { type: Object, default: () => ({}) },
      phases: { type: Array, default: () => [] },
      mode: { type: Boolean, default: false },
      resetKey: { type: Number, default: 0 },
    },

    data () {
      return { localRows: [], liveComp: {} } // liveComp: cumplimiento en vivo por fila (para la barra al teclear)
    },

    computed: {
      // Cumplimiento global = media del cumplimiento de las fases; OSH abiertas = suma de abiertas.
      globalCompliance () {
        const vals = this.localRows
          .filter((r) => r.compliance !== '' && r.compliance != null)
          .map((r) => this.num(r.compliance))
        if (!vals.length) return null
        return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length)
      },
      oshOpen () {
        return this.localRows.reduce((s, r) => s + this.num(r.openIncidents), 0)
      },
      tiles () {
        const gc = this.globalCompliance
        const obs = this.num(this.val('openObservations'))
        const lti = this.num(this.val('daysWithoutLti'))
        return [
          { key: 'globalCompliance', label: 'prm_dash_safety_compliance', derived: true,
            value: gc != null ? gc + ' %' : '-', color: gc == null ? '#1e3046' : gc >= 90 ? '#1e8449' : gc >= 75 ? '#b9770e' : '#c0392b' },
          { key: 'inspections', label: 'prm_dash_safety_inspections' },
          { key: 'openObservations', label: 'prm_dash_safety_observations', cls: obs > 0 ? 'c-amber' : '' },
          { key: 'oshOpen', label: 'prm_dash_safety_osh_open', derived: true,
            value: String(this.oshOpen), color: this.oshOpen > 0 ? '#c0392b' : '#1e8449' },
          { key: 'daysWithoutLti', label: 'prm_dash_safety_days_lti', cls: this.val('daysWithoutLti') === '' ? '' : lti >= 45 ? 'c-green' : 'c-red' },
          { key: 'nextAudit', label: 'prm_dash_safety_next_audit', type: 'date' },
        ]
      },
      statusOptions () {
        return PHASE_STATUSES.map((v) => ({ value: v, label: this.$t('prm_dash_phase_status_' + v) }))
      },
      blank () {
        return { phase: '', status: 'not_started', compliance: 0, openIncidents: 0, closedIncidents: 0, lastReview: '' }
      },
    },

    watch: {
      resetKey: { immediate: true, handler () { this.initLocal() } },
    },

    methods: {
      initLocal () {
        this.localRows = (this.phases || []).map((r) => ({ ...r, _k: ++keySeq }))
        this.liveComp = {}
      },
      val (key) { return (this.safety || {})[key] == null ? '' : this.safety[key] },
      num (v) { return Number(v) || 0 },
      phaseColor (r) { return phaseColor(r) },
      // Color de la barra de cumplimiento según el ESTADO de la fase (como el diseño).
      stateColor (r) {
        return { completed: SEM.green, in_progress: SEM.amber, not_started: '#9ca3af' }[r.status] || '#9ca3af'
      },
      // Valor de la barra de cumplimiento: el que se está tecleando (si lo hay) o el guardado.
      setLive (k, v) { this.$set(this.liveComp, k, v) },
      compVal (row) {
        const l = this.liveComp[row._k]
        return this.num(l != null ? l : row.compliance)
      },
      onCell (i, key, value) {
        this.localRows[i][key] = value
        this.emit()
      },
      addRow () {
        this.localRows.push({ ...this.blank, _k: ++keySeq })
        this.emit()
      },
      removeRow (i) {
        this.localRows.splice(i, 1)
        this.emit()
      },
      emit () {
        const clean = this.localRows.map((r) => { const x = { ...r }; delete x._k; return x })
        this.$emit('save-list', clean)
      },
    },
  }
</script>

<style lang="scss" scoped>
  .tiles {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px;
    padding: 10px; border-bottom: 1px solid #eef0f3;
  }
  // Tile: etiqueta arriba (2 líneas), valor debajo (grande, coloreado por significado).
  .tile { display: flex; flex-direction: column; gap: 3px; border: 1px solid #eef0f3; background: #fafbfc; border-radius: 6px; padding: 7px 9px; }
  .tile.derived { background: #f3f8fd; border-color: #d9e6f2; }
  .tile-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; color: #6b7787; line-height: 1.25; min-height: 22px; }
  .tile-value { display: inline-flex; align-items: center; font-size: 14px; font-weight: 700; color: #1e3046; font-variant-numeric: tabular-nums; }

  .tbl-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #3d6b9a; color: #fff; font-size: 9px; text-transform: uppercase; letter-spacing: .03em; font-weight: 700; padding: 5px 8px; text-align: left; white-space: nowrap; }
  th.c { text-align: center; }
  td { border-bottom: 1px solid #eef0f3; padding: 3px 8px; font-size: 11px; color: #2c3a4d; vertical-align: middle; }
  td.c { text-align: center; }
  td.empty { text-align: center; color: #9aa4b2; padding: 12px; }
  // Celda de cumplimiento: nº (ancho fijo) + % + barra, en la misma línea y siempre alineados.
  td.comp { min-width: 180px; }
  .comp-in { display: flex; align-items: center; gap: 4px; }
  .comp-num { width: 40px; flex-shrink: 0; display: flex; justify-content: flex-end; }
  // Dentro del número, no dejamos que el subrayado vacío ensanche la celda (ancho fijo del bloque).
  .comp-num ::v-deep .mf-text.underline.empty { min-width: 28px; }
  .comp-num ::v-deep .mf, .comp-num ::v-deep .mf-view { max-width: 100%; }
  .comp-in .pct { color: #6b7787; flex-shrink: 0; }
  .comp-in .bar { flex: 1; min-width: 50px; }

  // Cebra + hover como las demás tablas; el ámbar solo aparece en modo "Fuente de datos".
  tbody tr:nth-child(even) td { background: #f9fafb; }
  tbody tr:hover td { background: #eef2f7; }
  table.mark tbody td { background: #fff8e6; }
  table.mark tbody tr:nth-child(even) td { background: #fdf3d0; }
  table.mark tbody tr:hover td { background: #fbecc0; }

  .row-remove { border: 0; background: transparent; color: #c0392b; cursor: pointer; opacity: .55; }
  .row-remove:hover { opacity: 1; }
  .tbl-footer { padding: 8px 10px; }
  .row-add {
    border: 1px dashed #b9cde0; background: #f6f9fc; color: #3d6b9a;
    font-size: 11px; font-weight: 600; padding: 4px 12px; border-radius: 6px; cursor: pointer;
  }
  .row-add:hover { background: #eef4fa; }
</style>
